import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Plug, RefreshCw, CheckCircle2, AlertCircle, Loader2, ArrowRight,
  ShoppingBag, IndianRupee, Hash, TrendingUp,
} from 'lucide-react';

/**
 * POS-Synced Orders Widget — surfaces yesterday's Petpooja data on the Dashboard.
 * Auto-hides if POS isn't connected (shows a soft CTA to connect instead).
 */
export const POSSyncWidget = ({ clientId, API }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
  });

  const fetchSummary = useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${API}/dashboard/${clientId}/pos-summary`, auth());
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, API]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/clients/${clientId}/integrations/petpooja/sync`, {}, auth());
      await fetchSummary();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-border rounded-md p-4 flex items-center gap-2 text-sm text-text-muted"
           data-testid="pos-widget-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading POS sync…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white border border-border rounded-md p-4 text-xs text-state-error"
           data-testid="pos-widget-error">
        {error || 'POS data unavailable'}
      </div>
    );
  }

  // ----- Not connected: soft CTA -----
  if (!data.connected) {
    return (
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-md p-4"
           data-testid="pos-widget-not-connected">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-orange-100 text-orange-700 flex-shrink-0">
              <Plug className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-orange-900">Connect your POS for live sync</h3>
              <p className="text-xs text-orange-800/80 mt-0.5">
                Skip manual uploads — connect Petpooja once and we'll pull sales, inventory, menu, and tax data automatically every night.
              </p>
            </div>
          </div>
          <Link to="/pos-integrations"
                className="inline-flex items-center gap-1 text-xs font-semibold text-orange-900 hover:text-orange-700 whitespace-nowrap"
                data-testid="pos-widget-connect-link">
            Connect Petpooja <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  // ----- Connected: show yesterday's data -----
  const o = data.orders || {};
  const fresh = data.last_sync_at ? timeSince(data.last_sync_at) : null;
  const stale = fresh && fresh.hours > 36;

  return (
    <div className="bg-white border border-border rounded-md p-4" data-testid="pos-widget-connected">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 pb-3 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-orange-100 text-orange-700">
            <Plug className="h-3.5 w-3.5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-text-primary flex items-center gap-2">
              POS-Synced Orders · Yesterday
              {data.last_sync_status === 'success' ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-state-success bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                  <CheckCircle2 className="h-2.5 w-2.5" /> Live
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                  <AlertCircle className="h-2.5 w-2.5" /> {data.last_sync_status || 'unknown'}
                </span>
              )}
            </div>
            <div className="text-[11px] text-text-muted">
              {data.yesterday_date} · Petpooja Rest <span className="font-mono">{data.restaurant_id_hint}</span>
              {fresh && (
                <span className={`ml-2 ${stale ? 'text-amber-600 font-semibold' : ''}`}>
                  · synced {fresh.label}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={syncNow}
          disabled={syncing}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:bg-primary/5 border border-border rounded px-2.5 py-1"
          data-testid="pos-widget-sync-btn"
        >
          {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </div>

      {/* metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Tile icon={Hash} label="Orders" value={o.count?.toLocaleString() ?? '0'} />
        <Tile icon={IndianRupee} label="Revenue"
              value={`₹${(o.revenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />
        <Tile icon={ShoppingBag} label="Items sold" value={o.item_count?.toLocaleString() ?? '0'} />
        <Tile icon={TrendingUp} label="Avg ticket"
              value={`₹${(o.avg_ticket || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />
      </div>

      {/* top items */}
      {o.top_items && o.top_items.length > 0 ? (
        <div data-testid="pos-widget-top-items">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1.5">
            Top items by quantity
          </div>
          <div className="space-y-1">
            {o.top_items.map((it, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-text-muted w-4">{i + 1}.</span>
                  <span className="text-text-primary">{it.name}</span>
                </div>
                <span className="font-mono text-text-secondary">×{it.qty}</span>
              </div>
            ))}
          </div>
        </div>
      ) : o.count === 0 ? (
        <div className="text-xs text-text-muted italic text-center py-3" data-testid="pos-widget-no-orders">
          No orders synced for yesterday yet. Try "Sync now" if Petpooja has updated.
        </div>
      ) : (
        <div className="text-[11px] text-text-muted italic">
          Item-level detail not present in synced orders.
        </div>
      )}
    </div>
  );
};

const Tile = ({ icon: Icon, label, value }) => (
  <div className="bg-background-secondary rounded-md px-3 py-2">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-0.5">
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className="text-base font-semibold text-text-primary tabular-nums">{value}</div>
  </div>
);

function timeSince(isoString) {
  const t = new Date(isoString).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = Date.now() - t;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return { label: `${mins}m ago`, hours: 0 };
  const hours = Math.floor(mins / 60);
  if (hours < 24) return { label: `${hours}h ago`, hours };
  const days = Math.floor(hours / 24);
  return { label: `${days}d ago`, hours };
}

export default POSSyncWidget;
